import React, { useState, useEffect } from 'react';
import { BookOpen, Target, Calendar, Trophy, Lock, Check, X, Loader2, ChevronRight, ChevronLeft, Award, Flame, Bot } from 'lucide-react';
import { toast } from 'sonner';
import { BackButton } from '../components/BackButton';
import DOMPurify from 'isomorphic-dompurify';

const Learn = () => {
  const [view, setView] = useState('overview'); // overview, path, lesson, quiz
  const [paths, setPaths] = useState([]);
  const [selectedPath, setSelectedPath] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResults, setQuizResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dailyChallenge, setDailyChallenge] = useState(null);
  const [progress, setProgress] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadPaths(),
        loadDailyChallenge(),
        loadProgress()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadPaths = async () => {
    try {
      const response = await fetch(`${API_URL}/api/platform/learning/paths`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPaths(data.paths);
      }
    } catch (error) {
      console.error('Failed to load paths:', error);
    }
  };

  const loadDailyChallenge = async () => {
    try {
      const response = await fetch(`${API_URL}/api/platform/daily-challenge`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDailyChallenge(data);
      }
    } catch (error) {
      console.error('Failed to load challenge:', error);
    }
  };

  const loadProgress = async () => {
    try {
      const response = await fetch(`${API_URL}/api/platform/learning/progress`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProgress(data);
      }
    } catch (error) {
      console.error('Failed to load progress:', error);
    }
  };

  const selectPath = async (pathId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/platform/learning/path/${pathId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedPath(data);
        setView('path');
      }
    } catch (error) {
      toast.error('Failed to load path');
    } finally {
      setLoading(false);
    }
  };

  const selectLesson = async (lessonId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/platform/learning/lesson/${lessonId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedLesson(data);
        setView(data.type === 'quiz' ? 'quiz' : 'lesson');
        setQuizAnswers({});
        setQuizResults(null);
      }
    } catch (error) {
      toast.error('Failed to load lesson');
    } finally {
      setLoading(false);
    }
  };

  const completeLesson = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/platform/learning/lesson/${selectedLesson.lesson_id}/complete`,
        { method: 'POST', credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.xp_result) {
          toast.success(`✅ Lesson complete! +${data.xp_result.xp_awarded} XP`);
        }
        // Reload path to show progress
        await selectPath(selectedLesson.path_id);
        setView('path');
      }
    } catch (error) {
      toast.error('Failed to complete lesson');
    } finally {
      setSubmitting(false);
    }
  };

  const submitQuiz = async () => {
    const totalQuestions = selectedLesson.questions.length;
    const answeredCount = Object.keys(quizAnswers).length;

    if (answeredCount < totalQuestions) {
      toast.error('Please answer all questions');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/platform/learning/lesson/${selectedLesson.lesson_id}/submit-quiz`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ answers: quizAnswers })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setQuizResults(data);
        
        if (data.passed) {
          toast.success(`🎉 Quiz passed! ${data.score}% - +${data.xp_result?.xp_awarded} XP`);
        } else {
          toast.error(`Score: ${data.score}%. You need 70% to pass. Review and try again!`);
        }
      }
    } catch (error) {
      toast.error('Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const submitDailyChallenge = async (answer) => {
    setSubmitting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/platform/daily-challenge/submit?challenge_id=${dailyChallenge.challenge_id}&answer=${answer}`,
        { method: 'POST', credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.correct) {
          const streakBonus = data.xp_result?.streak_bonus || 0;
          const newStreak = data.xp_result?.new_streak || 0;
          
          if (streakBonus > 0) {
            toast.success(`🔥 ${newStreak} day streak! +${data.xp_result.xp_awarded} XP (+${streakBonus} bonus)`, { duration: 4000 });
          } else {
            toast.success(`✅ Correct! +${data.xp_result.xp_awarded} XP`, { duration: 3000 });
          }
        } else {
          toast.error(`❌ ${data.explanation}`);
        }
        loadDailyChallenge();
        loadProgress();
      } else if (response.status === 400) {
        toast.info('Already completed today!');
      }
    } catch (error) {
      toast.error('Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  // ===== RENDER FUNCTIONS =====

  const renderOverview = () => (
    <div className="space-y-12">
      {/* Daily Challenge */}
      {dailyChallenge && (
        <div className="bg-gradient-to-br from-[#0F3227] to-[#071A14] border-2 border-[#C9A227] rounded-xl p-8" data-testid="daily-challenge-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-6 h-6 text-[#C9A227]" />
              <h2 className="text-2xl font-heading font-bold">Hand of the Day</h2>
            </div>
            {dailyChallenge.completed && (
              <span className="px-3 py-1 bg-green-600 text-white rounded-full text-xs font-bold flex items-center gap-1">
                <Check className="w-3 h-3" />
                Completed
              </span>
            )}
            {progress && progress.current_streak > 0 && (
              <div className="flex items-center gap-2 px-3 py-1 bg-orange-600/20 border border-orange-500/30 rounded-full">
                <Flame className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-bold text-orange-500">{progress.current_streak} day streak</span>
              </div>
            )}
          </div>

          <p className="text-lg mb-6">{dailyChallenge.description}</p>

          {/* Scenario */}
          <div className="bg-[#121923] rounded-lg p-6 mb-6 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-foreground/60">Your Hand</p>
                <p className="text-xl font-bold text-[#C9A227]">{dailyChallenge.scenario.hand}</p>
              </div>
              <div>
                <p className="text-xs text-foreground/60">Position</p>
                <p className="text-xl font-bold">{dailyChallenge.scenario.position}</p>
              </div>
              <div>
                <p className="text-xs text-foreground/60">Board</p>
                <p className="text-xl font-bold">{dailyChallenge.scenario.board}</p>
              </div>
              <div>
                <p className="text-xs text-foreground/60">Pot / Bet to Call</p>
                <p className="text-xl font-bold">${dailyChallenge.scenario.pot} / ${dailyChallenge.scenario.bet_to_call}</p>
              </div>
            </div>
          </div>

          {/* Answer Options */}
          {!dailyChallenge.completed ? (
            <div>
              <p className="text-sm text-foreground/60 mb-3">What would you do?</p>
              <div className="flex gap-3">
                {['fold', 'call', 'raise'].map((action) => (
                  <button
                    key={action}
                    onClick={() => submitDailyChallenge(action)}
                    disabled={submitting}
                    data-testid={`challenge-answer-${action}`}
                    className="flex-1 py-3 rounded-lg font-semibold bg-[#1A2432] text-foreground hover:bg-[#C9A227] hover:text-[#0B0F14] transition-all disabled:opacity-50"
                  >
                    {action.charAt(0).toUpperCase() + action.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-green-600/10 border border-green-600/30 rounded-lg p-4">
              <p className="text-green-400 font-semibold mb-2">✅ Challenge completed!</p>
              <p className="text-sm text-foreground/60">Your answer: <span className="font-bold text-foreground">{dailyChallenge.user_answer}</span></p>
              <p className="text-xs text-foreground/50 mt-2">Come back tomorrow for a new challenge!</p>
            </div>
          )}
        </div>
      )}

      {/* Learning Paths */}
      <div>
        <h2 className="text-2xl font-heading font-bold mb-6">Learning Paths</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {paths.map((path) => (
            <div
              key={path.path_id}
              data-testid={`learning-path-${path.path_id}`}
              className="relative bg-[#121923] border border-white/10 rounded-xl overflow-hidden hover:border-[#C9A227]/50 transition-all group cursor-pointer"
              onClick={() => !path.locked && selectPath(path.path_id)}
            >
              {/* Gradient Header */}
              <div className={`h-24 bg-gradient-to-br ${path.color} relative`}>
                {path.locked && (
                  <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                    <Lock className="w-8 h-8 text-white" />
                  </div>
                )}
              </div>

              <div className="p-6">
                <h3 className="text-xl font-heading font-bold mb-2">{path.title}</h3>
                <p className="text-sm text-foreground/60 mb-4">{path.description}</p>
                
                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-foreground/60 mb-1">
                    <span>{path.completed_lessons} / {path.total_lessons} lessons</span>
                    <span>{path.completion_percent}%</span>
                  </div>
                  <div className="w-full h-2 bg-[#0F1419] rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#C9A227] transition-all duration-500"
                      style={{ width: `${path.completion_percent}%` }}
                    />
                  </div>
                </div>

                {path.requires_premium && (
                  <div className="px-3 py-1 bg-[#C9A227]/20 text-[#C9A227] rounded-full text-xs font-bold inline-block">
                    Premium Only
                  </div>
                )}
                
                {!path.locked && (
                  <div className="mt-4 text-[#C9A227] text-sm font-semibold flex items-center gap-1">
                    Continue Learning
                    <ChevronRight className="w-4 h-4" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      {progress && (
        <div className="bg-[#121923] border border-white/10 rounded-xl p-6">
          <h2 className="text-xl font-heading font-bold mb-4">Your Learning Progress</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-[#C9A227]">{progress.total_lessons_completed}</p>
              <p className="text-xs text-foreground/60">Lessons Completed</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <Flame className="w-8 h-8 text-orange-500" />
                <p className="text-3xl font-bold text-orange-500">{progress.current_streak}</p>
              </div>
              <p className="text-xs text-foreground/60">Day Streak</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-[#C9A227]">{progress.longest_streak}</p>
              <p className="text-xs text-foreground/60">Best Streak</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-[#C9A227]">{progress.paths?.beginner?.percent || 0}%</p>
              <p className="text-xs text-foreground/60">Beginner Path</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderPath = () => {
    if (!selectedPath) return null;

    return (
      <div className="space-y-8">
        {/* Header with prominent back button */}
        <div>
          <button
            onClick={() => setView('overview')}
            className="inline-flex items-center gap-2 px-4 py-2 mb-4 text-foreground/80 hover:text-[#C9A227] hover:bg-white/5 rounded-lg transition-all font-semibold"
            data-testid="path-back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Back to All Paths</span>
          </button>
          <h1 className="text-3xl font-heading font-bold mb-2">{selectedPath.path.title}</h1>
          <p className="text-foreground/60">{selectedPath.path.description}</p>
        </div>

        {/* Levels */}
        {selectedPath.levels.map((level, idx) => (
          <div key={level.level_id} className="bg-[#121923] border border-white/10 rounded-xl p-6" data-testid={`level-${level.level_number}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-heading font-bold">Level {level.level_number}: {level.title}</h2>
                <p className="text-sm text-foreground/60">{level.description}</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-[#C9A227]">{level.completion_percent}%</div>
                <div className="text-xs text-foreground/60">Complete</div>
              </div>
            </div>

            {/* Lessons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {level.lessons.map((lesson) => (
                <button
                  key={lesson.lesson_id}
                  onClick={() => !lesson.locked && selectLesson(lesson.lesson_id)}
                  disabled={lesson.locked}
                  data-testid={`lesson-${lesson.lesson_id}`}
                  className={`
                    p-4 rounded-lg text-left transition-all
                    ${lesson.completed 
                      ? 'bg-green-600/10 border border-green-600/30' 
                      : lesson.locked
                      ? 'bg-[#0F1419] border border-white/5 opacity-50 cursor-not-allowed'
                      : 'bg-[#0F1419] border border-white/10 hover:border-[#C9A227]/50'
                    }
                  `}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {lesson.type === 'quiz' ? (
                          <Target className="w-4 h-4 text-[#C9A227]" />
                        ) : (
                          <BookOpen className="w-4 h-4 text-[#C9A227]" />
                        )}
                        <h3 className="font-semibold text-sm">{lesson.title}</h3>
                      </div>
                      <p className="text-xs text-foreground/60">{lesson.duration_minutes} min • +{lesson.xp_reward} XP</p>
                    </div>
                    {lesson.completed && (
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    )}
                    {lesson.locked && (
                      <Lock className="w-5 h-5 text-foreground/30 flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderLesson = () => {
    if (!selectedLesson) return null;

    return (
      <div className="max-w-3xl mx-auto">
        {/* Header with prominent back button */}
        <button
          onClick={() => setView('path')}
          className="inline-flex items-center gap-2 px-4 py-2 mb-6 text-foreground/80 hover:text-[#C9A227] hover:bg-white/5 rounded-lg transition-all font-semibold"
          data-testid="lesson-back-btn"
        >
          <ChevronLeft className="w-5 h-5" />
          <span>Back to Path</span>
        </button>

        <div className="bg-[#121923] border border-white/10 rounded-xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <BookOpen className="w-8 h-8 text-[#C9A227]" />
            <div>
              <h1 className="text-2xl font-heading font-bold">{selectedLesson.title}</h1>
              <p className="text-sm text-foreground/60">{selectedLesson.duration_minutes} min • +{selectedLesson.xp_reward} XP</p>
            </div>
          </div>

          {/* Lesson Content */}
          <div className="prose prose-invert max-w-none mb-8">
            <div 
              className="text-foreground/90 leading-relaxed space-y-4"
              dangerouslySetInnerHTML={{ 
                __html: DOMPurify.sanitize(
                  selectedLesson.content
                    .replace(/^# /gm, '<h1 class="text-2xl font-heading font-bold text-[#C9A227] mt-6 mb-3">')
                    .replace(/^## /gm, '<h2 class="text-xl font-heading font-semibold text-[#C9A227] mt-5 mb-2">')
                    .replace(/^### /gm, '<h3 class="text-lg font-semibold mt-4 mb-2">')
                    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-foreground font-bold">$1</strong>')
                    .replace(/\n\n/g, '</p><p class="mb-4">')
                  .replace(/^(.+)$/gm, '<p class="mb-2">$1</p>'))
              }}
            />
          </div>

          {/* Complete Button */}
          {!selectedLesson.completed && (
            <button
              onClick={completeLesson}
              disabled={submitting}
              data-testid="complete-lesson-btn"
              className="w-full py-4 bg-[#C9A227] text-[#0B0F14] rounded-lg font-bold hover:bg-[#E1C25B] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Mark as Complete
                </>
              )}
            </button>
          )}

          {selectedLesson.completed && (
            <div className="bg-green-600/10 border border-green-600/30 rounded-lg p-4 flex items-center justify-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              <span className="text-green-400 font-semibold">Lesson Completed!</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderQuiz = () => {
    if (!selectedLesson) return null;

    return (
      <div className="max-w-3xl mx-auto">
        {/* Header with prominent back button */}
        <button
          onClick={() => {
            setView('path');
            setQuizResults(null);
          }}
          className="inline-flex items-center gap-2 px-4 py-2 mb-6 text-foreground/80 hover:text-[#C9A227] hover:bg-white/5 rounded-lg transition-all font-semibold"
          data-testid="quiz-back-btn"
        >
          <ChevronLeft className="w-5 h-5" />
          <span>Back to Path</span>
        </button>

        <div className="bg-[#121923] border border-white/10 rounded-xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <Target className="w-8 h-8 text-[#C9A227]" />
            <div>
              <h1 className="text-2xl font-heading font-bold">{selectedLesson.title}</h1>
              <p className="text-sm text-foreground/60">{selectedLesson.questions?.length} questions • +{selectedLesson.xp_reward} XP</p>
            </div>
          </div>

          {/* Quiz Questions */}
          {!quizResults ? (
            <div className="space-y-8">
              {selectedLesson.questions.map((question, idx) => (
                <div key={question.question_id} className="bg-[#0F1419] rounded-lg p-6 border border-white/5" data-testid={`question-${idx + 1}`}>
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-8 h-8 rounded-full bg-[#C9A227]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-[#C9A227]">{idx + 1}</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{question.question}</p>
                      {question.type === 'scenario' && (
                        <span className="inline-block mt-2 px-2 py-1 bg-blue-600/20 text-blue-400 rounded text-xs">Scenario</span>
                      )}
                    </div>
                  </div>

                  {/* Options */}
                  <div className="space-y-2 ml-11">
                    {question.options.map((option) => (
                      <label
                        key={option}
                        className={`
                          block p-3 rounded-lg cursor-pointer transition-all
                          ${quizAnswers[question.question_id] === option
                            ? 'bg-[#C9A227] text-[#0B0F14] font-semibold'
                            : 'bg-[#121923] hover:bg-[#1A2432] border border-white/10'
                          }
                        `}
                      >
                        <input
                          type="radio"
                          name={question.question_id}
                          value={option}
                          checked={quizAnswers[question.question_id] === option}
                          onChange={(e) => setQuizAnswers({...quizAnswers, [question.question_id]: e.target.value})}
                          className="hidden"
                          data-testid={`quiz-option-${question.question_id}-${option}`}
                        />
                        <span className="text-sm">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              {/* Submit Button */}
              <button
                onClick={submitQuiz}
                disabled={submitting || Object.keys(quizAnswers).length < selectedLesson.questions.length}
                data-testid="submit-quiz-btn"
                className="w-full py-4 bg-[#C9A227] text-[#0B0F14] rounded-lg font-bold hover:bg-[#E1C25B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Submit Quiz
                  </>
                )}
              </button>
            </div>
          ) : (
            /* Quiz Results */
            <div className="space-y-6">
              {/* Score Card */}
              <div className={`
                p-6 rounded-xl text-center
                ${quizResults.passed 
                  ? 'bg-green-600/10 border-2 border-green-600/30' 
                  : 'bg-red-600/10 border-2 border-red-600/30'
                }
              `}>
                <div className="text-5xl font-bold mb-2">
                  {quizResults.score}%
                </div>
                <p className="text-lg font-semibold mb-2">
                  {quizResults.passed ? '🎉 Quiz Passed!' : '📚 Keep Studying'}
                </p>
                <p className="text-sm text-foreground/60">
                  {quizResults.correct_count} / {quizResults.total_questions} correct
                  {quizResults.passed && quizResults.xp_result && (
                    <span className="text-[#C9A227] font-bold"> • +{quizResults.xp_result.xp_awarded} XP</span>
                  )}
                </p>
              </div>

              {/* AI Recommendation */}
              {quizResults.ai_recommendation && (
                <div className="bg-[#0F1419] border border-[#C9A227]/30 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Bot className="w-5 h-5 text-[#C9A227]" />
                    <h3 className="font-semibold text-[#C9A227]">Coach's Advice</h3>
                  </div>
                  <p className="text-foreground/80 leading-relaxed">{quizResults.ai_recommendation}</p>
                </div>
              )}

              {/* Question Review */}
              <div className="space-y-4">
                {quizResults.results.map((result, idx) => (
                  <div 
                    key={result.question_id} 
                    className={`
                      p-4 rounded-lg border
                      ${result.correct 
                        ? 'bg-green-600/5 border-green-600/30' 
                        : 'bg-red-600/5 border-red-600/30'
                      }
                    `}
                  >
                    <div className="flex items-start gap-3 mb-2">
                      <div className={`
                        w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0
                        ${result.correct ? 'bg-green-600' : 'bg-red-600'}
                      `}>
                        {result.correct ? (
                          <Check className="w-4 h-4 text-white" />
                        ) : (
                          <X className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm mb-2">{result.question}</p>
                        {!result.correct && (
                          <div className="space-y-1 text-xs">
                            <p className="text-red-400">Your answer: {result.your_answer}</p>
                            <p className="text-green-400">Correct answer: {result.correct_answer}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Explanation */}
                    <div className="ml-9 mt-2 p-3 bg-[#121923] rounded border border-white/5">
                      <p className="text-xs text-foreground/70">{result.explanation}</p>
                    </div>

                    {/* AI Hint */}
                    {result.ai_hint && (
                      <div className="ml-9 mt-2 p-3 bg-[#C9A227]/10 border border-[#C9A227]/30 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <Bot className="w-3 h-3 text-[#C9A227]" />
                          <span className="text-xs font-semibold text-[#C9A227]">AI Hint</span>
                        </div>
                        <p className="text-xs text-foreground/80">{result.ai_hint}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                {quizResults.passed ? (
                  <button
                    onClick={() => setView('path')}
                    className="flex-1 py-3 bg-[#C9A227] text-[#0B0F14] rounded-lg font-bold hover:bg-[#E1C25B] transition-colors"
                    data-testid="continue-learning-btn"
                  >
                    Continue Learning
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => setView('path')}
                      className="flex-1 py-3 bg-[#1A2432] text-foreground rounded-lg font-semibold hover:bg-[#1A2432]/70 transition-colors border border-white/10"
                    >
                      Review Lessons
                    </button>
                    <button
                      onClick={() => {
                        setQuizResults(null);
                        setQuizAnswers({});
                      }}
                      data-testid="retake-quiz-btn"
                      className="flex-1 py-3 bg-[#C9A227] text-[#0B0F14] rounded-lg font-bold hover:bg-[#E1C25B] transition-colors"
                    >
                      Retake Quiz
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0F14] via-[#0A241C] to-[#071A14] py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Main Header (only show in overview) */}
        {view === 'overview' && (
          <div className="text-center mb-12">
            <h1 className="text-4xl font-heading font-bold mb-2 flex items-center justify-center gap-3">
              <BookOpen className="w-10 h-10 text-[#C9A227]" />
              Learn & Improve
            </h1>
            <p className="text-foreground/60">Structured training to level up your game</p>
          </div>
        )}

        {/* Content Views */}
        {loading && view === 'overview' ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#C9A227] animate-spin" />
          </div>
        ) : (
          <>
            {view === 'overview' && renderOverview()}
            {view === 'path' && renderPath()}
            {view === 'lesson' && renderLesson()}
            {view === 'quiz' && renderQuiz()}
          </>
        )}
      </div>
    </div>
  );
};

export default Learn;
